import type { ObjectDirective } from 'vue';

// 埋点事件类型
export enum BURIED_EVENT_TYPE {
  CLICK = 'click',
  EXPOSURE = 'exposure',
  CUSTOM = 'custom',
}

// 埋点配置接口
export interface BuriedCoreConfig {
  autoTrack?: boolean;
  enableClick?: boolean;
  enableExposure?: boolean;
  exposureThreshold?: number;
  customEvents?: Record<string, unknown>;
  enableDevLog?: boolean;
  enableWebVitals?: boolean;
  webVitalsConfig?: {
    reportAllChanges?: boolean;
    durationThreshold?: number;
  };
  clickBlacklist?: {
    tagNames?: string[]; // 标签名黑名单，如 ['svg', 'path']
    classNames?: string[]; // 类名黑名单
    selectors?: string[]; // CSS选择器黑名单
  };
}

// 埋点数据接口
export interface BuriedData {
  event_type: BURIED_EVENT_TYPE;
  event_name: string;
  element_id?: string;
  element_class?: string;
  element_tag?: string;
  element_text?: string;
  element_path?: string;
  module_name?: string;
  custom_data?: Record<string, unknown>;
}

// 指令值类型
export type BuriedDirectiveValue =
  | string
  | {
      eventName?: string;
      customData?: Record<string, unknown>;
    };

export interface TrackingAdapter {
  name: string;
  init?: () => void;
  track: (eventName: string, data: Record<string, unknown>) => void;
  login?: (userId: number | string, username?: string) => void;
}

// 默认配置
const DEFAULT_CONFIG: Required<Omit<BuriedCoreConfig, 'customEvents'>> & {
  customEvents: Record<string, unknown>;
} = {
  autoTrack: true,
  enableClick: true,
  enableExposure: true,
  exposureThreshold: 0.5,
  customEvents: {},
  enableDevLog: true,
  enableWebVitals: true,
  webVitalsConfig: {
    reportAllChanges: false,
    durationThreshold: 40,
  },
  // 配置点击黑名单
  clickBlacklist: {
    tagNames: ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon'],
    classNames: [],
    selectors: [],
  },
};

// 核心埋点实现
export class BuriedCore {
  private config: BuriedCoreConfig;
  private exposureObserver: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private exposedElements: WeakSet<HTMLElement> = new WeakSet();
  private clickElements: WeakSet<HTMLElement> = new WeakSet();
  private isInitialized = false;
  private readonly send: (data: BuriedData) => void;
  private readonly trackingAdapters: TrackingAdapter[];
  private clickListener: ((event: MouseEvent) => void) | null = null;

  constructor(options: {
    config?: BuriedCoreConfig;
    send: (data: BuriedData) => void;
    trackingAdapters?: TrackingAdapter[];
  }) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...options.config,
    };
    this.send = options.send;
    this.trackingAdapters = options.trackingAdapters ?? [];
  }

  // 初始化埋点
  public init(): void {
    if (this.isInitialized || import.meta.server) {
      return;
    }

    this.isInitialized = true;

    if (this.config.autoTrack) {
      this.initializeTrackingAdapters();
      this.initExposureTracking();
      this.initClickTracking();
      if (this.config.enableExposure) {
        this.observeNewElements();
      }
      if (this.config.enableWebVitals) {
        this.initWebVitals();
      }
    }
  }

  // 销毁埋点
  public destroy(): void {
    if (this.exposureObserver) {
      this.exposureObserver.disconnect();
      this.exposureObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener, true);
      this.clickListener = null;
    }

    this.exposedElements = new WeakSet();
    this.clickElements = new WeakSet();
    this.isInitialized = false;
  }

  // 手动埋点
  public track(eventName: string, customData?: Record<string, unknown>): void {
    const buriedData: BuriedData = {
      event_type: BURIED_EVENT_TYPE.CUSTOM,
      event_name: eventName,
      ...customData,
    };

    this.sendBuriedData(buriedData);
  }

  // 用户登录
  public login(userId: number | string, username?: string): void {
    if (import.meta.server) return;
    this.trackingAdapters.forEach(adapter => {
      adapter.login?.(userId, username);
    });
  }

  // 重新观察所有埋点元素
  public reobserveElements(): void {
    if (!this.exposureObserver) return;

    this.exposureObserver.disconnect();
    this.observeElements();
  }

  // 获取配置
  public getConfig(): BuriedCoreConfig {
    return { ...this.config };
  }

  // 更新配置
  public updateConfig(newConfig: Partial<BuriedCoreConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };
  }

  // 获取埋点参数
  public getBuriedParams(element: HTMLElement): Record<string, unknown> {
    const paramsAttr = element.getAttribute('data-buried-params');
    if (!paramsAttr) {
      return {};
    }

    try {
      return JSON.parse(paramsAttr);
    } catch (_error) {
      try {
        const params = new URLSearchParams(paramsAttr);
        const result: Record<string, unknown> = {};
        for (const [key, value] of params.entries()) {
          result[key] = value;
        }
        return result;
      } catch (_urlError) {
        console.warn('Failed to parse data-buried-params:', paramsAttr);
        return { raw_params: paramsAttr };
      }
    }
  }

  // 创建埋点指令
  public createDirective(): ObjectDirective<HTMLElement, BuriedDirectiveValue> {
    return {
      mounted: (el, binding) => {
        if (import.meta.server) return;

        const { value } = binding;
        const eventName = typeof value === 'string' ? value : value?.eventName || 'CUSTOM_EVENT';
        const customData = typeof value === 'object' && value ? value.customData : undefined;

        const buriedParams = this.getBuriedParams(el);

        // 创建点击处理函数，保存引用以便清理
        const clickHandler = () => {
          const mergedData: Record<string, unknown> = {
            ...buriedParams,
            ...customData,
          };
          this.track(eventName, mergedData);
        };

        el.addEventListener('click', clickHandler);
        // 将处理函数保存到元素上，以便在卸载时清理
        (el as any)._buriedClickHandler = clickHandler;
      },
      unmounted: el => {
        if (import.meta.server) return;
        // 清理事件监听器
        const clickHandler = (el as any)._buriedClickHandler;
        if (clickHandler) {
          el.removeEventListener('click', clickHandler);
          delete (el as any)._buriedClickHandler;
        }
      },
    };
  }

  private initWebVitals(): void {
    if (import.meta.server) return;

    import('web-vitals')
      .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
        const config = this.config.webVitalsConfig || DEFAULT_CONFIG.webVitalsConfig;
        const { reportAllChanges, durationThreshold } = config;

        const reportMetric = (metric: any) => {
          this.track(`web_vitals_${metric.name}`, {
            metric_name: metric.name,
            metric_value: metric.value,
            metric_rating: metric.rating,
            metric_delta: metric.delta,
            metric_id: metric.id,
            navigation_type: metric.navigationType,
            timestamp: Date.now(),
          });
        };

        onCLS(metric => reportMetric(metric), { reportAllChanges });
        onLCP(metric => reportMetric(metric), { reportAllChanges });
        onINP(metric => reportMetric(metric), { reportAllChanges, durationThreshold });
        onFCP(metric => reportMetric(metric), { reportAllChanges });
        onTTFB(metric => reportMetric(metric), { reportAllChanges });
      })
      .catch(err => {
        console.warn('Failed to load web-vitals:', err);
      });
  }

  // 初始化曝光埋点
  private initExposureTracking(): void {
    if (!this.config.enableExposure) return;

    this.exposureObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            if (!this.exposedElements.has(element)) {
              this.exposedElements.add(element);
              this.trackExposure(element);
            }
          }
        });
      },
      {
        threshold: this.config.exposureThreshold,
        rootMargin: '0px',
      },
    );

    this.observeElements();
  }

  // 初始化点击埋点
  private initClickTracking(): void {
    if (!this.config.enableClick) return;
    if (this.clickListener) return;

    this.clickListener = event => {
      const target = event.target as HTMLElement | null;
      if (target) {
        this.trackClick(target);
      }
    };

    document.addEventListener('click', this.clickListener, true);
  }

  // 查找有效的点击目标元素
  private findValidClickTarget(element: HTMLElement): HTMLElement | null {
    let currentElement: HTMLElement | null = element;

    // 向上遍历DOM树，直到找到不在黑名单中的元素
    while (currentElement) {
      if (!this.isInClickBlacklist(currentElement)) {
        return currentElement;
      }
      currentElement = currentElement.parentElement;
    }

    return null;
  }

  // 检查元素是否在黑名单中
  private isInClickBlacklist(element: HTMLElement): boolean {
    const blacklist = this.config.clickBlacklist || DEFAULT_CONFIG.clickBlacklist;

    // 检查标签名
    if (blacklist.tagNames?.some(tag => element.tagName.toLowerCase() === tag.toLowerCase())) {
      return true;
    }

    // 检查类名
    if (blacklist.classNames?.some(className => element.classList.contains(className))) {
      return true;
    }

    // 检查CSS选择器
    if (
      blacklist.selectors?.some(selector => {
        try {
          return element.matches(selector);
        } catch {
          return false;
        }
      })
    ) {
      return true;
    }

    return false;
  }
  // 观察当前元素
  private observeElements(): void {
    const elements = document.querySelectorAll('[data-buried]');
    elements.forEach(element => {
      this.exposureObserver?.observe(element);
    });
  }

  // 观察新元素
  private observeNewElements(): void {
    if (!this.config.enableExposure) {
      return;
    }

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            if (element.hasAttribute('data-buried')) {
              this.exposureObserver?.observe(element);
            }

            const buriedElements = element.querySelectorAll('[data-buried]');
            buriedElements.forEach(buriedElement => {
              this.exposureObserver?.observe(buriedElement);
            });
          }
        });

        mutation.removedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            if (element.hasAttribute('data-buried')) {
              this.exposureObserver?.unobserve(element);
            }

            const buriedElements = element.querySelectorAll('[data-buried]');
            buriedElements.forEach(buriedElement => {
              this.exposureObserver?.unobserve(buriedElement);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.mutationObserver = observer;
  }

  // 获取元素的 XPath
  private getElementXPath(element: HTMLElement): string {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }

    const parts: string[] = [];
    let current: HTMLElement | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 0;
      let sibling: Element | null = current.previousElementSibling;

      // 计算同类型兄弟元素中的位置
      while (sibling) {
        if (sibling.nodeName === current.nodeName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      const tagName = current.nodeName.toLowerCase();
      const firstClass = current.classList.item(0);
      const pathIndex = index > 0 ? `[${index + 1}]` : '';
      parts.unshift(`${tagName}${firstClass ? `.${firstClass}` : ''}${pathIndex}`);

      current = current.parentElement;
    }

    return parts.length ? `${parts.join(' > ')}` : '';
  }

  // 追踪点击
  private trackClick(element: HTMLElement): void {
    // 检查是否命中黑名单
    const targetElement = this.findValidClickTarget(element);

    if (!targetElement) {
      return;
    }

    if (this.clickElements.has(targetElement)) {
      return;
    }

    this.clickElements.add(targetElement);
    setTimeout(() => {
      this.clickElements.delete(targetElement!);
    }, 1000);

    const buriedData = this.getBuriedData(targetElement, BURIED_EVENT_TYPE.CLICK);
    this.sendBuriedData(buriedData);
  }

  // 追踪曝光
  private trackExposure(element: HTMLElement): void {
    const buriedData = this.getBuriedData(element, BURIED_EVENT_TYPE.EXPOSURE);
    this.sendBuriedData(buriedData);
  }

  // 发送数据
  private sendBuriedData(data: BuriedData): void {
    if (this.config.enableDevLog && process.env.NODE_ENV === 'development') {
      console.debug('Buried Event:', data);
      return;
    }
    const adapterEventName = this.getAdapterEventName(data);
    this.trackingAdapters.forEach(adapter => {
      adapter.track(adapterEventName, {
        ...data,
        buried_event_type: data.event_type,
      });
    });
    this.send(data);
  }

  // 初始化第三方追踪
  private initializeTrackingAdapters(): void {
    if (import.meta.server) return;
    this.trackingAdapters.forEach(adapter => {
      adapter.init?.();
    });
  }

  private getAdapterEventName(data: BuriedData): string {
    if (data.event_type === BURIED_EVENT_TYPE.CUSTOM) {
      return data.event_name;
    }
    return `global_buried_${data.event_type}`;
  }

  // 获取元素文本
  private getElementText(element: HTMLElement): string {
    return element.textContent?.trim() || element.title || '';
  }

  // 获取模块上下文
  private getModuleContext(element: HTMLElement): {
    module_name: string;
    buried_params: string;
  } {
    let currentElement: HTMLElement | null = element;
    const contactedBuriedNames: string[] = [];
    const buriedParamsMap: Record<string, string> = {};

    while (currentElement) {
      const moduleName = currentElement.dataset.buried;
      const buriedParams = currentElement.dataset.buriedParams;
      if (moduleName) {
        contactedBuriedNames.push(moduleName);
        if (buriedParams) {
          buriedParamsMap[moduleName] = buriedParams;
        }
      }
      currentElement = currentElement.parentElement;
    }

    return {
      module_name: contactedBuriedNames.join('|'),
      buried_params: JSON.stringify(buriedParamsMap),
    };
  }

  // 获取埋点数据
  private getBuriedData(
    element: HTMLElement,
    eventType: BURIED_EVENT_TYPE,
    customData?: Record<string, unknown>,
  ): BuriedData {
    const buriedParams = this.getBuriedParams(element);
    const { module_name, buried_params } = this.getModuleContext(element);

    return {
      event_type: eventType,
      event_name: this.getEventName(element, eventType),
      element_id: element.id || element.getAttribute('name') || undefined,
      element_class: element.className || undefined,
      element_tag: element.tagName.toLowerCase(),
      element_text: this.getElementText(element),
      element_path: this.getElementXPath(element),
      module_name,
      // 平铺customData 和 buriedParams
      ...{
        ...buriedParams,
        ...customData,
        ...JSON.parse(buried_params),
      },
    };
  }

  // 获取事件名称
  private getEventName(element: HTMLElement, eventType: BURIED_EVENT_TYPE): string {
    const buriedAttr = element.getAttribute('data-buried');
    if (buriedAttr) {
      return buriedAttr;
    }

    const prefix = eventType === BURIED_EVENT_TYPE.CLICK ? 'CLICK' : 'EXPOSURE';
    const elementType = element.tagName.toLowerCase();
    const elementId = element.getAttribute('name') || element.id || element.className || 'unknown';

    return `${prefix}_${elementType.toUpperCase()}_${elementId.toString().toUpperCase()}`;
  }
}
